import { CloudIcon } from "assets/icons/CloudIcon";
import { DeleteIcon } from "assets/icons/DeleteIcon";
import { ExclamationIcon } from "assets/icons/ExclamationIcon";
import { ExpandIcon } from "assets/icons/ExpandIcon";
import { LISTA_COLORES_ICONOS } from "assets/icons/LISTA_COLORES_ICONOS";
import { showErrorToast } from "components/CustomAlerts/CustomToast";
import { IconButton } from "components/IconButton/IconButton";
import { Loader } from "components/Loader/Loader";
import React, { useRef, useState } from "react";

interface Props {
  title?: string;
  errors?: any;
  touched?: any;
  onBlur?: any;
  id: string;
  onChange: any;
  values: any;
  disabled?: boolean;
  onlyRead?: boolean;
  required?: boolean;
}

const shortenFileName = (file: File & { nombre_real?: string }, maxLength: number) => {
  // Por compatibilidad hacia atras se deja el nombre pasado como opcion pero la prioridad es nombre_real
  const fileName = file.nombre_real || file.name;
  if (fileName.length <= maxLength) return fileName;

  const extension = fileName.split(".").pop();
  if (!extension) return fileName;
  const nameWithoutExtension = fileName.slice(0, -(extension?.length + 1));

  const shortenedName = nameWithoutExtension.slice(
    0,
    maxLength - (extension?.length + 4),
  );

  return `${shortenedName}...${extension}`;
};

export const InputMultiFile = ({
  title,
  onChange = () => {},
  values,
  disabled,
  id,
  onlyRead,
  required,
  touched,
  errors,
}: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDragginOver, setIsDragginOver] = useState<boolean>(false);

  const showError = touched && touched[id] && errors && errors[id];
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleImageUpload = (file: File | null) => {
    if (!file || file.type !== "application/pdf") {
      showErrorToast("Solo se permiten archivos PDF.");
      return;
    }

    setIsLoading(true);
    setTimeout(() => {
      const prevFiles = values[id] ?? [];
      const fileEvent = { target: { name: id, value: [...prevFiles, file] } };
      onChange(fileEvent);
      setIsLoading(false);
    }, 500);
  };

  const removeFile = (e: any, file: File) => {
    e.preventDefault();
    e.stopPropagation();

    const files = values[id] ?? [];
    const newFiles = files.filter((ctFile: File) => ctFile.name !== file.name);
    const fileEvent = { target: { name: id, value: newFiles } };
    onChange(fileEvent);

    // 💡 Resetear el input para permitir volver a subir el mismo archivo
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target.files && e.target.files.length > 0) {
      handleImageUpload(e.target.files[0]);
    }
  };

  const showFile = (e: any, file: File & { fakeId?: any; isVirtual?: boolean; nombre_real?: string }) => {
    e.preventDefault();
    e.stopPropagation();
    // Para incorporar el visor en esta vista, se debe bifurcar los caminos, uno para el pdf en s3 y otro para el pdf local que todavia no fue subido, el pdf en la nube se vera con el visor nuevo, en cambio el local seguira con el blob
    if (!file) return;

    if (file.isVirtual && file.fakeId) {
      // flujo con el visor nuevo.
      const displayName = file.nombre_real || file.name;
      window.open(`/visor-pdf/${file.name}?id=${file.fakeId}&fileName=${encodeURIComponent(displayName)}`, "_blank");
    } else {
      // flujo con el blob local, se mantiene igual.
      // Una mejora seria que usar el mismo visor para los archivos locales, posible pero requiere entender un poco mas la docu del visor para no romper el flujo.
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    }
  };

  const handleDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target.id === `select-listener-${id}`) {
      if (e.currentTarget.contains(e.relatedTarget as Node)) {
        return;
      }
      setIsDragginOver(true);
    }
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target.id === `select-listener-${id}`) {
      if (e.currentTarget.contains(e.relatedTarget as Node)) {
        return;
      }
      setIsDragginOver(false);
    }
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragginOver(false);
    const file = e.dataTransfer.files[0];

    if (file && file.type === "application/pdf") {
      handleImageUpload(file);
    } else {
      showErrorToast("Solo se permiten archivos PDF.");
    }
  };

  return (
    <>
      <label className={`select-label ${showError ? "error" : ""}`}>
        <div className="d-flex justify-content-between">
          {title && (
            <h5 className="label">
              {title} {required && "*"}
            </h5>
          )}
        </div>

        <div
          id={`select-listener-${id}`}
          className={`input-file input-container ${isDragginOver ? "draggin-over" : ""}`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isLoading && (
            <>
              <Loader
                style={{ margin: "0" }}
                spinnerStyle={{ width: "20px", height: "20px" }}
              ></Loader>
              <p className="input-file-text">Cargando documento</p>
            </>
          )}

          {isDragginOver && (
            <>
              <CloudIcon size={24} />
              <p className="input-file-text">
                Arrastra tus archivos para adjuntar
              </p>
            </>
          )}

          {!isLoading && !isDragginOver && (
            <>
              <CloudIcon size={24} />
              <p className="input-file-text">
                Hace click para adjuntar el documento
              </p>
            </>
          )}
          <input
            ref={fileInputRef} // 👈 Agregamos la referencia
            id={`select-input-${id}`}
            style={{ display: "none" }}
            type="file"
            accept=".pdf"
            onChange={handleInputChange}
          />
        </div>
      </label>

      {values[id] && values[id].length > 0 && (
        <>
          {values[id].map((file: File & { nombre_real?: string }, index: number) => (
            <div key={`${file.name}-${index}`} className="input-file-selected">
              <p className="title">{shortenFileName(file, 50)}</p>
              <IconButton onClick={(e: any) => showFile(e, file)}>
                <ExpandIcon size={23} />
              </IconButton>
              <IconButton
                variant="error"
                onClick={(e: any) => removeFile(e, file)}
              >
                <DeleteIcon size={25} color="#C93B3B" />
              </IconButton>
            </div>
          ))}
        </>
      )}

      {showError && (
        <div className="d-flex align-items-center gap-1 mt-1">
          <ExclamationIcon size={14} color={LISTA_COLORES_ICONOS.ERROR} />
          <p className="component-error-msg mb-0">{errors[id]}</p>
        </div>
      )}
    </>
  );
};
